import {useLocation} from '@github-ui/react-router'
import {type ReactNode, useCallback} from 'react'

import {SavedViewConfigurationProvider} from '../../contexts/views/SavedViewConfigurationProvider'
import type {ViewConfiguration, ViewConfigurationOverrides} from '../../contexts/views/view-configuration'
import {FieldIdentifier} from './fields'
import {useSavedViewFields} from './use-saved-view-fields'
import {useSavedViewUrlConfiguration} from './use-saved-view-url-configuration'

const TEMP_DEFAULTS: ViewConfiguration = {
  layout: 'list',
  groupBy: FieldIdentifier.LABEL,
  swimlaneBy: null,
  sorts: [],
  visibleFields: [FieldIdentifier.LABEL, FieldIdentifier.ASSIGNEE, FieldIdentifier.MILESTONE],
}

/**
 * Builds the `applied` configuration from what the server actually resolved. Reading it
 * from URL overrides alone would lie whenever the server ignores or falls back on an
 * override (github/views#211): the menu would report what the URL asked for instead of
 * what is on the page. Visibility comes from the persisted baseline, and so does sort when the
 * payload does not state one. Legacy payloads without configuration keep their preview defaults
 * and cannot save configuration.
 */
export function viewConfigurationFromPayload(
  {
    sorts,
    ...payloadConfig
  }: {
    layout: ViewConfiguration['layout']
    groupBy: string | null
    swimlaneBy: string | null
    sorts?: ViewConfiguration['sorts']
  },
  baseline?: ViewConfiguration,
): ViewConfiguration {
  const fallback = baseline ?? TEMP_DEFAULTS
  return {...fallback, ...payloadConfig, sorts: sorts ?? fallback.sorts}
}

/**
 * The layout a URL that states none inherits.
 *
 * The saved one, and for a payload carrying no saved configuration the same default
 * `useSavedViewUrlActions` is handed below. Both have to read it the same way, or
 * dropping the parameter and deciding what dropping it meant would disagree.
 */
export function baselineLayout(baseline?: ViewConfiguration): ViewConfiguration['layout'] {
  return (baseline ?? TEMP_DEFAULTS).layout
}

/**
 * A saved view's configuration, read from what the server served and written to the URL. Keyed by
 * the page, so another view starts from its own.
 */
export function SavedViewUrlConfiguration({
  applied,
  baseline,
  basePath,
  pendingSearch,
  onPrefetch,
  children,
}: {
  applied: ViewConfiguration
  baseline?: ViewConfiguration
  basePath: string
  /** The search of a navigation on this view that is still loading. */
  pendingSearch?: string
  /**
   * Handler that is called with the URL a change the reader is likely to make would load.
   * Returns whether it sent a request.
   */
  onPrefetch?: (url: string) => boolean
  children: ReactNode
}) {
  const {pathname} = useLocation()
  const {data: fields} = useSavedViewFields(basePath)
  const urlConfiguration = useSavedViewUrlConfiguration({
    applied,
    baseline: baseline ?? TEMP_DEFAULTS,
    fields,
    pendingSearch,
  })
  const {urlForChanges} = urlConfiguration
  const prefetch = useCallback(
    (changes: ViewConfigurationOverrides) => {
      const url = urlForChanges(changes)
      return url !== null && onPrefetch !== undefined && onPrefetch(url)
    },
    [onPrefetch, urlForChanges],
  )

  return (
    <SavedViewConfigurationProvider
      key={pathname}
      baseline={baseline}
      appliedConfiguration={urlConfiguration.appliedConfiguration}
      onConfigurationChange={urlConfiguration.onConfigurationChange}
      onConfigurationIntent={prefetch}
      onReset={urlConfiguration.onReset}
    >
      {children}
    </SavedViewConfigurationProvider>
  )
}
